/**
 * HydrologyKernel — the AssemblyScript port of TerrainHydrology + the thermal
 * eroder (assembly/hydrology_kernel.ts), embedded as base64 and installed
 * behind a module-level hook. When active, `analyzeHydrology` (fused: fill →
 * route → order → accumulate, one boundary crossing), `computeFlowDirections`,
 * `orderByDescendingHeight` and `thermalErode` route here and return the SAME
 * BITS the TypeScript produces (tests/unit/hydrology-kernel.test.ts gates it
 * with Object.is); anything the kernel cannot take (arena growth refused)
 * falls back to the TypeScript.
 *
 * Routing is decided by measurement (`pnpm bench:wasm`, ≥2× gate): the
 * standalone `fillDepressions` (1.3× — a binary heap whose pop order on ties
 * must match the oracle's, so no better heap shape is admissible) and
 * `computeFlowAccumulation` (0.4 ms of work) are NOT routed on their own even
 * though the kernel implements them for the fused call; `erodeDroplets` was
 * ported, measured at 1.06×, and removed.
 *
 * Dual-source law: a change to the hydrology/erosion math is made in BOTH the
 * TypeScript oracle and assembly/hydrology_kernel.ts, then `pnpm build:wasm`
 * and the parity test. The oracle is the semantics; this is the speed.
 *
 * Boot: `await preloadHydrologyKernel()` once (Atlas does it from main.ts and
 * the water bake worker). Before that resolves — or when wasm is missing —
 * every call is plain TypeScript. `setHydrologyKernel(null)` opts out.
 */

import { Logger } from '../core/Logger.js';
import { instantiateEmbeddedWasm, wasmAvailable } from '../shared/wasm.js';
import { HYDROLOGY_WASM_B64 } from './hydrologyKernel.wasm.js';

interface HydrologyExports extends WebAssembly.Exports {
  memory: WebAssembly.Memory;
  reserve(bytes: number): number;
  fillDepressions(hp: number, outp: number, closedp: number, heapp: number, n: number, epsilon: number): void;
  computeFlowDirections(hp: number, recvp: number, n: number): void;
  orderByDescendingHeight(hp: number, orderp: number, keysp: number, tkeysp: number, torderp: number, histp: number, n: number): void;
  computeFlowAccumulation(recvp: number, orderp: number, accp: number, total: number): void;
  analyzeHydrology(
    hp: number, filledp: number, recvp: number, orderp: number, accp: number,
    closedp: number, heapp: number, keysp: number, tkeysp: number, torderp: number, histp: number,
    n: number, epsilon: number,
  ): void;
  thermalErode(hp: number, deltap: number, n: number, iterations: number, talus: number, amount: number, cellSize: number): void;
}

/** Thermal config mirror (kept structural so this file never imports the oracle). */
export interface HydrologyThermalConfig {
  iterations?: number; talusSlope?: number; amount?: number; cellSize?: number;
}

/** Largest grid the kernel will take (cells); bigger fields stay on the TypeScript path. */
const MAX_CELLS = 1 << 24;
/** Radix histogram bytes (4 passes × 256 buckets × u32) — mirrors assembly/lib/radix32.ts. */
const HIST_BYTES = 4 * 256 * 4;
/** Fused analysis result (mirrors TerrainHydrology.HydrologyResult structurally). */
export interface HydrologyKernelResult {
  filled: Float32Array; receivers: Int32Array; accumulation: Float32Array; order: Int32Array;
}

const align16 = (v: number): number => (v + 15) & ~15;

export class HydrologyKernel {
  private constructor(private readonly ex: HydrologyExports) {}

  /** Instantiate the embedded module (async — see shared/wasm.ts). */
  static async create(): Promise<HydrologyKernel> {
    const ex = await instantiateEmbeddedWasm<HydrologyExports>(HYDROLOGY_WASM_B64);
    return new HydrologyKernel(ex);
  }

  /**
   * Lay `sizes` (bytes, in order) out in the arena; returns their pointers or
   * null when linear memory refuses to grow. Views MUST be created after this
   * call — growth detaches the previous ArrayBuffer.
   */
  private layout(sizes: number[]): number[] | null {
    let total = 0;
    for (const s of sizes) total += align16(s);
    const base = this.ex.reserve(total) >>> 0;
    if (base === 0) return null;
    const ptrs: number[] = [];
    let p = base;
    for (const s of sizes) {
      ptrs.push(p);
      p += align16(s);
    }
    return ptrs;
  }

  private f32(ptr: number, len: number): Float32Array { return new Float32Array(this.ex.memory.buffer, ptr, len); }
  private i32(ptr: number, len: number): Int32Array { return new Int32Array(this.ex.memory.buffer, ptr, len); }

  fillDepressions(height: Float32Array, n: number, epsilon: number): Float32Array | null {
    const t = n * n;
    if (t > MAX_CELLS || height.length < t) return null;
    const p = this.layout([t * 4, t * 4, t, t * 16]);
    if (!p) return null;
    this.f32(p[0], t).set(height.subarray(0, t));
    this.ex.fillDepressions(p[0], p[1], p[2], p[3], n, epsilon);
    return new Float32Array(this.f32(p[1], t));
  }

  computeFlowDirections(height: Float32Array, n: number): Int32Array | null {
    const t = n * n;
    if (t > MAX_CELLS || height.length < t) return null;
    const p = this.layout([t * 4, t * 4]);
    if (!p) return null;
    this.f32(p[0], t).set(height.subarray(0, t));
    this.ex.computeFlowDirections(p[0], p[1], n);
    return new Int32Array(this.i32(p[1], t));
  }

  orderByDescendingHeight(height: Float32Array, n: number): Int32Array | null {
    const t = n * n;
    if (t > MAX_CELLS || height.length < t) return null;
    const p = this.layout([t * 4, t * 4, t * 4, t * 4, t * 4, HIST_BYTES]);
    if (!p) return null;
    this.f32(p[0], t).set(height.subarray(0, t));
    this.ex.orderByDescendingHeight(p[0], p[1], p[2], p[3], p[4], p[5], n);
    return new Int32Array(this.i32(p[1], t));
  }

  computeFlowAccumulation(height: Float32Array, receivers: Int32Array, n: number, order?: Int32Array): Float32Array | null {
    const t = n * n;
    if (t > MAX_CELLS || receivers.length < t) return null;
    const ord = order ?? this.orderByDescendingHeight(height, n);
    if (!ord || ord.length < t) return null;
    const p = this.layout([t * 4, t * 4, t * 4]);
    if (!p) return null;
    this.i32(p[0], t).set(receivers.subarray(0, t));
    this.i32(p[1], t).set(ord.subarray(0, t));
    this.ex.computeFlowAccumulation(p[0], p[1], p[2], t);
    return new Float32Array(this.f32(p[2], t));
  }

  /** Fused fill → route → order → accumulate; null when the kernel declined. */
  analyzeHydrology(height: Float32Array, n: number, epsilon: number): HydrologyKernelResult | null {
    const t = n * n;
    if (t > MAX_CELLS || height.length < t) return null;
    //           h     filled recv  order acc   closed heap    keys  tkeys torder hist
    const p = this.layout([t * 4, t * 4, t * 4, t * 4, t * 4, t, t * 16, t * 4, t * 4, t * 4, HIST_BYTES]);
    if (!p) return null;
    this.f32(p[0], t).set(height.subarray(0, t));
    this.ex.analyzeHydrology(p[0], p[1], p[2], p[3], p[4], p[5], p[6], p[7], p[8], p[9], p[10], n, epsilon);
    return {
      filled: new Float32Array(this.f32(p[1], t)),
      receivers: new Int32Array(this.i32(p[2], t)),
      order: new Int32Array(this.i32(p[3], t)),
      accumulation: new Float32Array(this.f32(p[4], t)),
    };
  }

  /** In-place thermal erosion; false when the kernel declined. */
  thermalErode(height: Float32Array, n: number, config: HydrologyThermalConfig = {}): boolean {
    const t = n * n;
    if (t > MAX_CELLS || height.length < t) return false;
    const p = this.layout([t * 4, t * 4]);
    if (!p) return false;
    this.f32(p[0], t).set(height.subarray(0, t));
    this.ex.thermalErode(
      p[0], p[1], n,
      Math.max(1, Math.floor(config.iterations ?? 50)),
      config.talusSlope ?? Math.tan((33 * Math.PI) / 180),
      Math.min(0.5, Math.max(0, config.amount ?? 0.5)),
      config.cellSize ?? 1,
    );
    height.set(this.f32(p[0], t));
    return true;
  }
}

let _active: HydrologyKernel | null = null;
let _preload: Promise<boolean> | null = null;

/** Install (or, with null, remove) the kernel every hydrology/erosion call routes through. */
export function setHydrologyKernel(kernel: HydrologyKernel | null): void {
  _active = kernel;
}

/** The installed kernel, or null while on the TypeScript path. */
export function activeHydrologyKernel(): HydrologyKernel | null {
  return _active;
}

export function hydrologyKernelActive(): boolean {
  return _active !== null;
}

/**
 * Instantiate + install the kernel once; resolves true when active. Safe to
 * call from any thread; concurrent calls share one instantiation.
 */
export function preloadHydrologyKernel(): Promise<boolean> {
  if (_active) return Promise.resolve(true);
  if (_preload) return _preload;
  if (!wasmAvailable()) return Promise.resolve(false);
  _preload = HydrologyKernel.create()
    .then((k) => {
      if (!_active) _active = k;
      return true;
    })
    .catch((err: unknown) => {
      Logger.warn('HydrologyKernel', `wasm kernel unavailable, staying on TypeScript: ${String(err)}`);
      return false;
    })
    .finally(() => { _preload = null; });
  return _preload;
}
